import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAppSelector } from 'src/app/hooks';

import { Layout } from 'src/styles';
import { appDispatch } from 'src/utils/utils';
import { setsubmitSuccessTarget } from 'src/features/pageSlice';

import AsmubipButton from './AsmubipButton';
import { PageType } from 'src/constants/PageType';

export default function TopBarForm({ page }: { page: number }) {
  const [alertOpen, setAlertOpen] = useState(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const {
    id,
    title,
    key,
    type,
    formDisabled,
    formWizard,
    formIsValid,
    formValues,
    submitSuccessTarget,
  } = useAppSelector((state) => state.page);
  const location = useLocation();
  const navigate = useNavigate();

  const handleAlertOpen = () => {
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleCancelBack = () => {
    handleAlertClose();
  };

  const handleBack = () => {
    if (formWizard) {
      if (page === 1) {
        if (!formDisabled || !formIsValid) {
          handleAlertOpen();
          return;
        }
        navigate(['perfil', 'entreposto'].includes(key || '') ? '/' : `/${key}`);
        return;
      }
      const newPage = page - 1;
      const newTarget = `${location.pathname}?page=${newPage}`;
      appDispatch(setsubmitSuccessTarget(newTarget));
      navigate(newTarget);
      return;
    }

    if (!formDisabled) {
      handleAlertOpen();
      return;
    }

    handleDiscard();
  };

  const handleNext = () => {
    if (!formWizard || !formIsValid) {
      return;
    }
    const { pages, submitSuccessTarget: formWizardTarget } = formWizard;

    if (page === pages) {
      appDispatch(setsubmitSuccessTarget(formWizardTarget));
      return;
    }

    const newPage = page + 1;
    const newTarget = location.pathname.includes('/create')
      ? `${location.pathname.replace('/create', '/edit/{ID}')}?page=${newPage}`
      : `${location.pathname}?page=${newPage}`;
    appDispatch(setsubmitSuccessTarget(newTarget));
  };

  const handleDiscard = () => {
    if (formWizard || type === PageType.CREATE) {
      navigate(['perfil', 'entreposto'].includes(key || '') ? '/' : `/${key}`);
      return;
    }

    if (!submitSuccessTarget || submitSuccessTarget === -1) {
      navigate(-1);
      return;
    }

    if (typeof submitSuccessTarget === 'string') {
      navigate(submitSuccessTarget.replace('{ID}', `${id}`).split('?')[0]);
      return;
    }

    submitSuccessTarget(formValues || {});
  };

  return (
    <>
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          <Box sx={Layout.topBarWrapper}>
            <Box sx={Layout.topBar}>
              <IconButton edge="start" color="inherit" aria-label="voltar" onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
              <AsmubipButton
                ref={submitButtonRef}
                variant="text"
                color="inherit"
                aria-label="salvar"
                type="submit"
                onClick={!formWizard ? undefined : handleNext}
                sx={{ mt: 1 }}
                disabled={!formWizard && formDisabled}>
                {!formWizard ? 'Salvar' : page === formWizard.pages ? 'Finalizar' : 'Próximo'}
              </AsmubipButton>
            </Box>
            <Typography variant="h6" component="div" sx={Layout.topBarTitle}>
              {title}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Dialog open={alertOpen} onClose={handleAlertClose} maxWidth="md" fullWidth>
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">Você está saindo sem salvar as alterações.</Typography>
          <Typography variant="body1">
            Deseja continuar editando ou perder as alterações?
          </Typography>
        </DialogContent>
        <DialogActions>
          <AsmubipButton variant="outlined" onClick={handleDiscard}>
            Sair
          </AsmubipButton>
          <AsmubipButton variant="contained-dark" onClick={handleCancelBack}>
            Continuar editando
          </AsmubipButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
